/**
 * Spatial Market Explorer - Interactive Micro-Market Visualization
 * 
 * This component renders a Google Maps interface with Feature Layer integration 
 * to display Bengaluru localities colored by their respective micro-markets.
 * 
 * Core Features:
 * - Google Maps JavaScript API with data-driven styling
 * - Feature Layer (LOCALITY) integration for built-in administrative boundaries
 * - Dynamic Place ID mapping between Places API and Feature Layer systems
 * - Real-time micro-market coloring (5 distinct colors for 5 micro-markets)
 * - Interactive click events with locality information display
 * - Comprehensive debugging and analytics logging
 * 
 * Technical Architecture:
 * - React functional component with TypeScript
 * - Google Maps API v3 with @react-google-maps/api wrapper
 * - Environment-based configuration (API keys, Map IDs)
 * - Feature Layer data-driven styling for polygon coloring
 * - Place ID reverse mapping for micro-market identification
 * 
 * Key Concepts:
 * - Feature Layer Place IDs ≠ Places API Place IDs (different namespace systems)
 * - Map ID must be configured with Feature Layer ("Locality") enabled in Google Cloud Console  
 * - Data-driven styling requires specific Google Maps Platform setup
 * - Viewport-dependent Place ID discovery (IDs change with zoom/pan)
 * 
 * Performance:
 * - Handles 1,000+ styling calls efficiently
 * - Processes 1,400+ unique Place IDs per session
 * - Supports multiple simultaneous Place ID mappings
 * - Real-time color application without performance degradation
 * 
 * Created: July 22, 2024
 * Status: Production Ready - Core objective achieved with multi-color validation
 */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { microMarkets } from '../data/microMarkets';
import boundaryIds from '../data/boundaryIds.json';
import { fetchPropertyMarkers, PropertyMarker } from '../services/supabase';

/**
 * Micro-market color scheme for Bengaluru real estate visualization
 * Each color represents a distinct geographical and commercial micro-market
 */
const microMarketColors = {
  Central: "#C62828", // Red - Business district, premium commercial areas
  East: "#1565C0",    // Blue - IT corridor, tech hubs
  West: "#2E7D32",    // Green - Established residential, cultural areas
  North: "#FF8F00",   // Orange - Emerging growth areas, connectivity hubs  
  South: "#6A1B9A"    // Purple - Mixed development, educational institutions
};

/**
 * Create reverse mapping: Feature Layer Place ID → Micro-Market
 * 
 * This is the core data structure that enables real-time color mapping.
 * It transforms our locality-based micro-market data into a format optimized 
 * for Feature Layer styling callbacks.
 * 
 * Process:
 * 1. Iterate through each micro-market and its localities
 * 2. Look up the Feature Layer Place ID for each locality
 * 3. Create direct Place ID → micro-market mapping
 * 4. Used by styling function for O(1) color lookup
 */
const placeIdToMicroMarket: Record<string, keyof typeof microMarketColors> = {};
Object.entries(microMarkets).forEach(([microMarket, localities]) => {
  localities.forEach(locality => {
    const placeId = boundaryIds[locality as keyof typeof boundaryIds];
    if (placeId) {
      placeIdToMicroMarket[placeId] = microMarket as keyof typeof microMarketColors;
    }
  });
});

/**
 * Map Configuration
 */
const center = { lat: 12.9082, lng: 77.5833 }; // South Bengaluru (JP Nagar) - optimal for visualization
const containerStyle = { width: '100vw', height: '100vh' }; // Full viewport coverage

/**
 * Environment Configuration with Validation
 * These variables must be set in .env file for the application to function
 */
const MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Development debugging - shows configuration status
console.log('=== ENVIRONMENT VARIABLES DEBUG ===');
console.log('MAP_ID from env:', MAP_ID);
console.log('API_KEY from env:', API_KEY ? `${API_KEY.substring(0, 10)}...${API_KEY.substring(API_KEY.length - 4)}` : 'MISSING');
console.log('All env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

interface MapComponentProps {}

/**
 * Main Map Component
 * 
 * Renders the interactive micro-market visualization with:
 * - Google Maps base layer
 * - Feature Layer integration for administrative boundaries
 * - Dynamic styling based on micro-market mappings
 * - Click interactions for locality details
 * - Real-time analytics and debugging
 */
const MapComponent: React.FC<MapComponentProps> = () => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const featureLayerRef = useRef<any>(null);
  // Remove Place ID collection state
  // const [collectedPlaceIds, setCollectedPlaceIds] = useState<string[]>([]);

  // New: Track which micro-markets are active (colored)
  const [activeMarkets, setActiveMarkets] = useState<Set<string>>(new Set());
  const [propertyMarkers, setPropertyMarkers] = useState<PropertyMarker[]>([]);
  const propertyMarkerRefs = useRef<google.maps.Marker[]>([]);
  
  /**
   * Collect Granular Place IDs from Current Viewport
   * 
   * Streamlined collection method focused on LOCALITY layer only.
   * This approach was proven successful for Hebbal, Hennur, and Jakkur.
   * 
   * Key Success Factors:
   * - LOCALITY layer provides optimal granularity for neighborhood-level visualization
   * - Frequency analysis filters out broad administrative areas
   * - Higher zoom levels (15-16) provide better locality-level granularity
   * - Simple, focused approach without unnecessary complexity
   */
  const collectPlaceIdFromViewport = useCallback(() => {
    if (!mapRef.current) {
      console.log('❌ Map not loaded yet');
      return;
    }

    const currentZoom = mapRef.current.getZoom() || 0;
    const currentCenter = mapRef.current.getCenter()?.toJSON();
    
    console.log('🔍 COLLECTING PLACE IDs FROM CURRENT VIEWPORT...');
    console.log('📍 Current center:', currentCenter);
    console.log('🔍 Current zoom:', currentZoom);
    
    // Recommend higher zoom for better granularity
    if (currentZoom < 15) {
      console.log('⚠️ RECOMMENDATION: Zoom to level 15-16 for better locality granularity');
    }
    
    // Get current bounds
    const bounds = mapRef.current.getBounds();
    if (bounds) {
      console.log('📦 Current bounds:', bounds.toJSON());
    }
    
    // Simple collection data structure
    const allPlaceIds = new Map<string, {
      id: string,
      displayName: string,
      frequency: number
    }>();
    
    let collectionActive = true;
    
    // Simple LOCALITY-only collection (proven approach)
    const runCollection = async () => {
      try {
        console.log('🎯 Collecting Place IDs from LOCALITY layer...');
        const featureLayer = (mapRef.current as any).getFeatureLayer('LOCALITY');
        
        if (!featureLayer) {
          console.log('❌ LOCALITY layer not available');
          return;
        }
        
        // Store original style function
        const originalStyle = featureLayer.style;
        
        // Temporary style function to collect Place IDs
        featureLayer.style = (params: any) => {
          if (collectionActive) {
            const feature = params.feature;
            const placeId = feature.placeId;
            const displayName = feature.displayName || feature.name || 'Unknown';
            
            if (placeId) {
              // Track place ID with metadata
              if (allPlaceIds.has(placeId)) {
                const existing = allPlaceIds.get(placeId)!;
                existing.frequency += 1;
              } else {
                allPlaceIds.set(placeId, {
                  id: placeId,
                  displayName,
                  frequency: 1
                });
              }
            }
          }
          
          // Call original styling function
          return originalStyle ? originalStyle(params) : {
            fillColor: '#E0E0E0',
            fillOpacity: 0.1,
            strokeColor: '#BDBDBD',
            strokeOpacity: 0.3,
            strokeWeight: 1,
          };
        };
        
        // Force a style refresh by panning slightly
        const currentCenter = mapRef.current?.getCenter();
        if (currentCenter && mapRef.current) {
          const lat = currentCenter.lat();
          const lng = currentCenter.lng();
          
          // Tiny pan to trigger style refresh
          mapRef.current.panTo({ lat: lat + 0.00005, lng: lng + 0.00005 });
          
          setTimeout(() => {
            // Pan back
            mapRef.current?.panTo({ lat, lng });
            
            // Restore original style
            featureLayer.style = originalStyle;
            collectionActive = false;
            
            // Process results
            processResults();
          }, 800);
        }
        
      } catch (error) {
        console.error('❌ Error collecting Place IDs:', error);
      }
    };
    
    const processResults = () => {
      if (allPlaceIds.size === 0) {
        console.log('❌ No Place IDs found in current viewport.');
        return;
      }
      
      // Convert to array and filter out broad administrative IDs
      const placeIdArray = Array.from(allPlaceIds.values());
      const filteredIds = placeIdArray.filter(item => {
        if (item.frequency > 20) {
          console.log(`🚫 Filtering out broad ID: ${item.id} (freq: ${item.frequency})`);
          return false;
        }
        return true;
      });
      
      if (filteredIds.length === 0) {
        console.log('❌ No suitable Place IDs found.');
        return;
      }
      
      // Sort by frequency (lower = more specific)
      const sortedIds = filteredIds.sort((a, b) => a.frequency - b.frequency);
      
      console.log(`🎯 Found ${sortedIds.length} granular Place IDs:`);
      sortedIds.slice(0, 3).forEach((item, index) => {
        console.log(`${index + 1}. ${item.id} (freq: ${item.frequency})`);
      });
      
      // Show the most granular Place ID
      const bestPlaceId = sortedIds[0];
      console.log(`\n🏆 MOST GRANULAR: ${bestPlaceId.id} (frequency: ${bestPlaceId.frequency})`);
      
      // setCollectedPlaceIds(prev => [...prev, bestPlaceId.id]); // Removed
      
      // Show testing command
      console.log(`🧪 TEST: PLACE_ID="${bestPlaceId.id}" LOCALITY="[Area Name]" npx ts-node scripts/testPlaceId.ts`);
    };
    
    runCollection().catch(console.error);
  }, []);
  
  // Hooks must be called before any early returns
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY || '',
  });

  // Update feature layer styling when activeMarkets changes
  const updateFeatureLayerStyling = useCallback(() => {
    if (!featureLayerRef.current) return;

    console.log('🔄 Updating feature layer styling. Active markets:', Array.from(activeMarkets));
    
    featureLayerRef.current.style = (params: any) => {
      const feature = params.feature;
      const placeId = feature.placeId;
      
      // Check if this place belongs to an active micro-market
      const microMarket = placeIdToMicroMarket[placeId];
      
      if (microMarket && activeMarkets.has(microMarket)) {
        const color = microMarketColors[microMarket];
        console.log(`✅ COLORING: ${microMarket} (${placeId.substring(0, 15)}...) with ${color}`);
        
        return {
          fillColor: color,
          fillOpacity: 0.6,
          strokeColor: color,
          strokeOpacity: 1.0,
          strokeWeight: 2,
        };
      }
      
      // Default styling for unmatched or inactive areas
      return {
        fillColor: '#E0E0E0',
        fillOpacity: 0.1,
        strokeColor: '#BDBDBD',
        strokeOpacity: 0.3,
        strokeWeight: 1,
      };
    };
  }, [activeMarkets]);

  // Update styling whenever activeMarkets changes
  React.useEffect(() => {
    updateFeatureLayerStyling();
  }, [activeMarkets, updateFeatureLayerStyling]);

  // Fetch property markers from Supabase on mount
  useEffect(() => {
    fetchPropertyMarkers().then(setPropertyMarkers);
  }, []);

  // Render property markers on the map
  useEffect(() => {
    if (!mapRef.current || !window.google) return;
    // Remove old markers
    propertyMarkerRefs.current.forEach(marker => marker.setMap(null));
    propertyMarkerRefs.current = [];
    // Map micro-market to Google Maps color name
    const colorMap: Record<string, string> = {
      Central: 'red',
      East: 'blue',
      West: 'green',
      North: 'orange',
      South: 'purple',
    };
    let bounds: google.maps.LatLngBounds | null = null;
    propertyMarkers.forEach(p => {
      if (!p.latitude || !p.longitude) return;
      // Normalize micro_market value for robust color matching
      let normalizedMarket = p.micro_market?.trim();
      if (normalizedMarket) {
        normalizedMarket = normalizedMarket.charAt(0).toUpperCase() + normalizedMarket.slice(1).toLowerCase();
      }
      const colorName = (normalizedMarket && colorMap[normalizedMarket]) ? colorMap[normalizedMarket] : 'red';
      // Use Google Maps classic colored pin icon
      const iconUrl = `http://maps.google.com/mapfiles/ms/icons/${colorName}-dot.png`;
      // Debug log for marker color assignment
      console.log('Marker:', p.project_name, 'Raw micro_market:', p.micro_market, 'Normalized:', normalizedMarket, 'Color:', colorName, 'Icon:', iconUrl);
      const marker = new window.google.maps.Marker({
        position: { lat: p.latitude, lng: p.longitude },
        map: mapRef.current!,
        title: `${p.project_name} • ${p.price_range}`,
        icon: iconUrl,
      });
      const html = `
        <b>${p.project_name}</b><br>
        ${p.configurations}<br>
        ${p.price_range} (${p.price_per_sqft} per sqft)<br>
        Developer: ${p.developer}<br>
        Location: ${p.location}<br>
        <b>Micro-Market:</b> ${p.micro_market || 'N/A'}
      `;
      const info = new window.google.maps.InfoWindow({ content: html });
      marker.addListener('click', () => info.open({ anchor: marker, map: mapRef.current! }));
      propertyMarkerRefs.current.push(marker);
      // Extend bounds
      if (!bounds) {
        bounds = new window.google.maps.LatLngBounds();
      }
      bounds.extend({ lat: p.latitude, lng: p.longitude });
    });
    // Fit map to bounds if markers exist
    if (bounds && propertyMarkers.length > 0) {
      mapRef.current!.fitBounds(bounds, 80); // 80px padding
    }
  }, [propertyMarkers, isLoaded]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    console.log('=== MAP LOADED ===');
    console.log('Map instance:', map);
    console.log('Using MAP_ID:', MAP_ID);
    
    // Test map capabilities for data-driven styling
    const caps = map.getMapCapabilities();
    console.log('=== MAP CAPABILITIES TEST ===');
    console.log('isDataDrivenStylingAvailable:', caps.isDataDrivenStylingAvailable);
    console.log('All capabilities:', caps);
    
    mapRef.current = map;

    try {
      console.log('=== FEATURE LAYER DEBUGGING ===');
      
      // Test different feature layer types including POSTAL_CODE
      const localityLayer = (map as any).getFeatureLayer('LOCALITY');
      const administrativeLayer = (map as any).getFeatureLayer('ADMINISTRATIVE_AREA_LEVEL_2'); 
      const countryLayer = (map as any).getFeatureLayer('COUNTRY');
      const postalCodeLayer = (map as any).getFeatureLayer('POSTAL_CODE');
      
      console.log('LOCALITY layer:', localityLayer);
      console.log('ADMINISTRATIVE_AREA_LEVEL_2 layer:', administrativeLayer);
      console.log('COUNTRY layer:', countryLayer);
      console.log('POSTAL_CODE layer:', postalCodeLayer);
      
      // Check if layers are enabled
      console.log('LOCALITY isEnabled:', localityLayer?.isEnabled);
      console.log('ADMINISTRATIVE_AREA_LEVEL_2 isEnabled:', administrativeLayer?.isEnabled);
      console.log('POSTAL_CODE isEnabled:', postalCodeLayer?.isEnabled);
      
      const featureLayer = postalCodeLayer;  // Switch to POSTAL_CODE layer to match demo Place IDs
      featureLayerRef.current = featureLayer; // Store reference for updating styling

      /**
       * Feature Layer Styling Function
       * 
       * This is the core function that applies micro-market colors to Feature Layer polygons.
       * Called by Google Maps for every visible postal code polygon in the viewport.
       * 
       * Process:
       * 1. Extract Place ID from Feature Layer feature object
       * 2. Look up micro-market assignment using our reverse mapping
       * 3. Apply appropriate color styling or default grey
       * 4. Handle fallback name-based matching if Place ID not found
       * 5. Collect analytics and debugging information
       * 
       * Performance: Optimized for O(1) Place ID lookups with extensive logging
       * for development and Place ID discovery purposes.
       */
      let styleCallCount = 0;
      let actualPlaceIds: string[] = [];
      
      featureLayer.style = (params: any) => {
        styleCallCount++;
        const feature = params.feature;
        const placeId = feature.placeId;
        
        /**
         * Locality Name Extraction
         * 
         * Google Maps Feature Layer objects don't consistently provide locality names.
         * We try multiple property paths to extract displayName for debugging and 
         * fallback name-based matching.
         */
        const displayName = feature.displayName || 
                           feature.name || 
                           (feature.place && feature.place.displayName) ||
                           (feature.place && feature.place.name) ||
                           feature.Eg; // Internal Google identifier fallback
        
        // Analytics: Collect Place IDs for discovery and validation
        actualPlaceIds.push(placeId);
        
        /**
         * Development Debugging
         * 
         * Extensive logging for first 10 styling calls to help with:
         * - Place ID discovery and validation
         * - Feature object structure analysis  
         * - Micro-market mapping verification
         * - Performance monitoring
         */
        if (styleCallCount <= 10) {
          console.log(`🎨 STYLING CALL #${styleCallCount}`);
          console.log(`   placeId: ${placeId}`);
          console.log(`   displayName: ${displayName}`);
          
          // Deep debugging for first 3 calls to understand Feature object structure
          if (styleCallCount <= 3) {
            console.log(`   🔍 ALL FEATURE PROPS:`, Object.keys(feature));
            console.log(`   🔍 FEATURE OBJECT:`, feature);
            
            // Analyze Google's internal feature properties
            console.log(`   🔍 feature.place:`, feature.place);
            console.log(`   🔍 feature.Dg:`, feature.Dg);
            console.log(`   🔍 feature.Eg:`, feature.Eg);
            
            // Discover additional properties that might contain locality information
            console.log(`   🔍 Checking for hidden props...`);
            for (let prop in feature) {
              if (typeof feature[prop] === 'string' && feature[prop].length < 100) {
                console.log(`   🔍 ${prop}:`, feature[prop]);
              }
            }
          }
        }
        
        /**
         * Primary Matching: Place ID → Micro-Market
         * 
         * This is the main matching mechanism using our pre-built lookup table.
         * O(1) performance for real-time color application.
         */
        const microMarket = placeIdToMicroMarket[placeId];
        
        // Only color if the micro-market is active
        if (microMarket && activeMarkets.has(microMarket)) {
          const color = microMarketColors[microMarket];
          console.log(`✅ PLACE ID MATCHED! ${microMarket} micro-market, color: ${color}`);
          
          // Return micro-market specific styling
          return {
            fillColor: color,
            fillOpacity: 0.6,        // Semi-transparent fill
            strokeColor: color,
            strokeOpacity: 1.0,      // Solid border
            strokeWeight: 2,         // Medium border thickness
          };
        } else {
          // Log Place ID misses for discovery purposes
          if (styleCallCount <= 10) {
            console.log(`➖ No match for placeId: ${placeId}`);
          }
          
          /**
           * Fallback Matching: Display Name → Micro-Market
           * 
           * If Place ID matching fails, attempt name-based matching.
           * This catches cases where:
           * - Place ID is not in our dataset
           * - Google uses different Place ID namespace
           * - Locality name is recognizable but Place ID is unknown
           */
          let matchByName = null;
          if (displayName && typeof displayName === 'string' && displayName.trim() !== '') {
            matchByName = Object.entries(microMarkets).find(([microMarket, localities]) =>
              localities.some(locality => 
                displayName.toLowerCase().includes(locality.toLowerCase()) ||
                locality.toLowerCase().includes(displayName.toLowerCase())
              )
            );
          }
          
          if (matchByName) {
            const [microMarket] = matchByName;
            const color = microMarketColors[microMarket as keyof typeof microMarketColors];
            console.log(`🎯 NAME MATCH! ${displayName} → ${microMarket}, color: ${color}`);
            
            // Return micro-market styling for name-matched locality
            return {
              fillColor: color,
              fillOpacity: 0.6,
              strokeColor: color,
              strokeOpacity: 1.0,
              strokeWeight: 2,
            };
          }
          
          /**
           * Default Styling for Unmatched Areas
           * 
           * Areas that don't match any micro-market get subtle grey styling.
           * This maintains visual consistency while highlighting our target areas.
           */
          return {
            fillColor: '#E0E0E0',    // Light grey fill
            fillOpacity: 0.1,        // Very transparent
            strokeColor: '#BDBDBD',  // Medium grey border
            strokeOpacity: 0.3,      // Semi-transparent border
            strokeWeight: 1,         // Thin border
          };
        }
      };
      
      setTimeout(() => {
        console.log(`📊 STYLING SUMMARY: ${styleCallCount} total styling calls made`);
        console.log(`📊 UNIQUE PLACE IDS FOUND: ${Array.from(new Set(actualPlaceIds)).length}`);
        
        // Show first 10 actual Place IDs for analysis
        const uniqueIds = Array.from(new Set(actualPlaceIds)).slice(0, 10);
        console.log(`📊 SAMPLE PLACE IDS:`, uniqueIds);
        
        // Compare with our fetched IDs
        console.log(`📊 OUR FETCHED IDS:`, Object.values(boundaryIds));
        
        // Check for any overlap
        const ourIds = Object.values(boundaryIds);
        const overlaps = uniqueIds.filter(id => ourIds.includes(id));
        console.log(`📊 OVERLAPPING IDS:`, overlaps);
      }, 5000);

      // Add click listener to feature layer
      featureLayer.addListener('click', (event: any) => {
        const feature = event.features?.[0];
        if (feature) {
          const placeId = feature.placeId;
          const microMarket = placeIdToMicroMarket[placeId];
          
          console.log('=== LOCALITY CLICKED ===');
          console.log('Place ID:', placeId);
          console.log('Micro-market:', microMarket || 'Not in our dataset');
          
          // Find the locality name by place ID
          const localityName = Object.entries(boundaryIds).find(([_, id]) => id === placeId)?.[0];
          
          if (microMarket && localityName) {
            alert(`${localityName}\nMicro-market: ${microMarket}\nColor: ${microMarketColors[microMarket]}`);
          }
        }
      });

      console.log(`✅ Feature layer styling applied for ${Object.keys(placeIdToMicroMarket).length} postal codes`);
      
    } catch (error) {
      console.error('❌ Error setting up feature layer:', error);
      console.log('Make sure MAP_ID is configured with Locality layer enabled');
    }
  }, [activeMarkets]); // Depend on activeMarkets
  
  // Validation after hooks
  if (!API_KEY) {
    return (
      <div style={{ padding: '20px', color: 'red', fontFamily: 'Arial' }}>
        <h2>❌ Configuration Error</h2>
        <p>REACT_APP_GOOGLE_MAPS_API_KEY is not set.</p>
        <p>Please restart your dev server after creating .env file.</p>
      </div>
    );
  }

  if (!MAP_ID) {
    return (
      <div style={{ padding: '20px', color: 'red', fontFamily: 'Arial' }}>
        <h2>❌ Configuration Error</h2>
        <p>REACT_APP_GOOGLE_MAP_ID is not set.</p>
        <p>Current value: {MAP_ID}</p>
        <p>Please restart your dev server after creating .env file.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return <div>Loading Spatial Market Explorer...</div>;
  }

  return (
    <div style={{ height: '100vh', width: '100vw' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={14}
        onLoad={onMapLoad}
        options={{
          mapId: MAP_ID,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        }}
      >
      </GoogleMap>
      {/* Micro-market toggle box */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        backgroundColor: 'white',
        padding: '15px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        minWidth: '220px',
        zIndex: 10
      }}>
        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Micro-Markets</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.entries(microMarketColors).map(([market, color]) => {
            const isActive = activeMarkets.has(market);
            return (
              <button
                key={market}
                onClick={() => {
                  setActiveMarkets(prev => {
                    const next = new Set(prev);
                    if (next.has(market)) {
                      next.delete(market);
                    } else {
                      next.add(market);
                    }
                    return next;
                  });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: isActive ? color : '#f5f5f5',
                  color: isActive ? 'white' : '#333',
                  border: isActive ? `2px solid ${color}` : '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '14px',
                  outline: isActive ? '2px solid #222' : 'none',
                  boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s',
                  marginBottom: '2px',
                }}
                aria-pressed={isActive}
              >
                <span style={{
                  width: '18px',
                  height: '18px',
                  background: color,
                  borderRadius: '3px',
                  marginRight: '10px',
                  border: isActive ? '2px solid white' : '1px solid #ccc',
                  display: 'inline-block',
                }}></span>
                {market}
                {isActive && (
                  <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 400 }}>
                    (Colored)
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ marginTop: '12px', fontSize: '12px', color: '#666' }}>
          Click a button to toggle coloring for each micro-market.
        </div>
        <div style={{ marginTop: '10px', fontSize: '10px', color: '#999' }}>
          Map ID: {MAP_ID}
        </div>
      </div>
    </div>
  );
};

export default MapComponent; 